import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Droplet, Shield, Gauge, Leaf, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const Descaling = () => {
  const benefits = [
    {
      icon: <Gauge className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />,
      title: "Improved Efficiency",
      description: "Enhance equipment performance and reduce energy consumption through professional descaling solutions"
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />,
      title: "Equipment Protection",
      description: "Extend machinery lifespan and prevent costly repairs with our specialized descaling chemicals"
    },
    {
      icon: <Droplet className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />,
      title: "Advanced Formulation",
      description: "State-of-the-art chemical solutions designed for optimal scale removal without damaging equipment"
    },
    {
      icon: <Leaf className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />,
      title: "Eco-Friendly",
      description: "Environmentally conscious descaling treatments that align with sustainability goals"
    }
  ];

  const processSteps = [
    'Initial Assessment',
    'Custom Solution Formulation',
    'Professional Application',
    'Quality Inspection'
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial Descaling Solutions",
    "description": "Professional chemical descaling services for industrial equipment including boilers, heat exchangers, and cooling systems",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    }
  };

  return (
    <>
      <Helmet>
        <title>Industrial Descaling Solutions | Chemical Descaling Services | Gortherm Energy</title>
        <meta name="description" content="Professional industrial descaling services for boilers, heat exchangers, and cooling systems. Chemical descaling solutions to improve efficiency, extend equipment life, and reduce energy consumption. Expert scale removal services." />
        <meta name="keywords" content="descaling services, chemical descaling, boiler descaling, heat exchanger cleaning, scale removal, industrial cleaning, equipment maintenance, chemical cleaning, descaling chemicals, cooling tower cleaning Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Industrial Descaling Solutions | Chemical Descaling Services | Gortherm Energy" />
        <meta property="og:description" content="Professional chemical descaling services to optimize your equipment's performance and efficiency. Expert industrial cleaning solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/descaling" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Descaling Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="Professional descaling services for improved equipment efficiency and extended lifespan." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/descaling" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <motion.header 
          className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-r from-blue-900 to-blue-700 text-white pt-16 sm:pt-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <motion.h1 
              className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4"
              {...fadeIn}
            >
              Industrial Descaling Solutions
            </motion.h1>
            <motion.p 
              className="text-base sm:text-xl lg:text-2xl max-w-2xl"
              {...fadeIn}
            >
              Professional chemical descaling services to optimize your equipment's performance and efficiency
            </motion.p>
          </div>
        </motion.header>

        {/* Benefits Section */}
        <section className="py-12 sm:py-16 px-4 sm:px-6 bg-gray-50">
          <div className="container mx-auto">
            <motion.h2 
              className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 text-gray-800"
              {...fadeIn}
            >
              Why Choose Our Descaling Services?
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {benefits.map((benefit, index) => (
                <motion.article
                  key={index}
                  className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="mb-3 sm:mb-4" aria-hidden="true">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-800">{benefit.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{benefit.description}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-12 sm:py-16 px-4 sm:px-6">
          <div className="container mx-auto">
            <motion.h2 
              className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 text-gray-800"
              {...fadeIn}
            >
              Our Descaling Process
            </motion.h2>
            <div className="max-w-3xl mx-auto">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  className="flex items-center mb-4 sm:mb-6"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="ml-3 sm:ml-4 flex-1 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">{step}</h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <motion.aside 
          className="py-12 sm:py-16 px-4 sm:px-6 bg-blue-900 text-white"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="container mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Ready to Optimize Your Equipment?</h2>
            <p className="text-base sm:text-xl mb-6 sm:mb-8">Contact us today for a customized descaling solution</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="bg-white text-blue-900 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center mx-auto text-sm sm:text-base w-full sm:w-auto">
                  Get Started
                  <ChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                </button>
              </a>
              <a href="tel:+9779802781400">
                <button className="bg-blue-800 hover:bg-blue-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center mx-auto text-sm sm:text-base w-full sm:w-auto">
                  <Phone className="mr-2 w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  Call Us Now
                </button>
              </a>
            </div>
            <p className="mt-4 text-sm sm:text-base">Call: <a href="tel:+9779802781400" className="font-semibold hover:underline">+977 980 278 1400</a></p>
          </div>
        </motion.aside>
      </main>
    </>
  );
};

export default Descaling;